<script setup>
/**
 * The lesson card (community-lesson DESIGN.md L1; redone 2026-10-28,
 * lesson-redo DESIGN.md B, H1, I): ONE component for the Community feed and
 * the share sheet's preview, so the preview really is what strangers will see.
 *
 * A two-line story with the lesson as the star:
 * - the picture at 3:2 (16:10 on a phone), or, when it can't be used, the
 *   four cards fanned on the plum table; the Mystic's square mark sits on its
 *   bottom-left corner (its tap opens the Mystic's page, never the lesson);
 * - the lead-in: *Someone asked:* “the asker's own question” (2 lines held);
 * - the lesson as the headline, italic (3 lines held, so a row lines up);
 * - the foot: "✦ Spoke to me · 12" (a toggle in place) and "Shared by Lark"
 *   with the sharer's round avatar (seekers are circles); "a seeker" never
 *   has one and is never a link.
 *
 * `preview` (the share sheet before publishing): the response unlit, no count,
 * no mark to tap. `wide`: full width. A tap anywhere else emits `open`.
 *
 * The picture opens (picture-caption rev 1, 2026-10-30): on the feed and the
 * profile tiles it is its own button (PictureOpen, its tap stopped), full size
 * with the lesson's caption over its foot. The card's one line stays the
 * lesson; the rest of the card still opens the lesson.
 *
 * The share sheet (rev 13, DESIGN.md §E5): `look` tells the switch's story,
 * `veiled` while off (the picture desaturated under a cream veil, the words at
 * 62%, no mark) and `clear` while on; either centres the lead-in and the lesson.
 * `editable` adds a "✎ Edit" chip at the lesson's corner and makes the lesson
 * itself a tap into edit (both emit `edit`); `editing` swaps the lesson for the
 * `edit` slot (the sheet's field, Cancel · Done) at full strength; `updated`
 * glows the card with "✦ Updated · the question and the card lines follow it".
 */
import { computed, ref, watch } from 'vue'
import {
  captionOf, creditAvatar, creditLine, creditName, creditSeeker, openCreditSeeker, hasPicture, lessonCards, leadIn, mysticOf, spokeFrom, SPOKE_SHORT,
} from '@/utils/lesson'
import { toggleReadingSupport } from '@/api-service'
import { track } from '@/utils/analytics'
import MysticMark from './MysticMark.vue'
import PictureOpen from './PictureOpen.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  lesson: { type: Object, required: true },
  preview: { type: Boolean, default: false },
  wide: { type: Boolean, default: false },
  // The feed's card is a button; the share sheet's is not.
  interactive: { type: Boolean, default: false },
  look: { type: String, default: '' },          // '' | 'veiled' | 'clear' (the share sheet)
  editable: { type: Boolean, default: false },
  editing: { type: Boolean, default: false },
  updated: { type: Boolean, default: false },
})
const emit = defineEmits(['open', 'edit'])

const picFailed = ref(false)
watch(() => props.lesson && props.lesson.image_url, () => { picFailed.value = false })
const showPic = computed(() => hasPicture(props.lesson) && !picFailed.value)
const cards = computed(() => lessonCards(props.lesson).slice(0, 5))
const failedArt = ref({})
// "Spoke to me" answers in place on the feed (Chris, 2026-10-27): a toggle, its
// tap its own, never the card's open. The share sheet's preview stays plain text.
const spoke = ref(!!(props.lesson && (props.lesson.spoke ?? props.lesson.supported)))
const count = ref(Number(props.lesson && (props.lesson.spoke_count ?? props.lesson.support_count)) || 0)
watch(() => props.lesson, (l) => {
  spoke.value = !!(l && (l.spoke ?? l.supported))
  count.value = Number(l && (l.spoke_count ?? l.support_count)) || 0
})
const canSpeak = computed(() => props.interactive && !props.preview)
const busy = ref(false)
async function toggleSpoke() {
  if (busy.value) return
  busy.value = true
  try {
    const r = spokeFrom(await toggleReadingSupport(props.lesson.short_uuid))
    if (r) {
      spoke.value = r.spoke
      count.value = r.count
      if (r.spoke) track('lesson_resonated', { item_id: props.lesson.short_uuid })
    }
  } finally { busy.value = false }
}
const lead = computed(() => leadIn(props.lesson))
const star = computed(() => String(props.lesson.lesson || '').trim())
const mystic = computed(() => mysticOf(props.lesson))
const credit = computed(() => creditLine(props.lesson))
// A named credit is a link to that seeker's profile (never in the share
// sheet's preview, never for "a seeker"); its tap is its own, not the card's.
const creditLink = computed(() => props.interactive && !props.preview && !!creditSeeker(props.lesson))
const who = computed(() => creditName(props.lesson))
// The sharer's avatar: only beside a named credit (credit_seeker), never "a seeker".
const named = computed(() => !!creditSeeker(props.lesson))
const avatar = computed(() => creditAvatar(props.lesson))
const avFailed = ref(false)
watch(avatar, () => { avFailed.value = false })
const initial = computed(() => (who.value || '✦').trim().charAt(0).toUpperCase())
function open() { if (props.interactive) emit('open', props.lesson) }
const veiled = computed(() => props.look === 'veiled')
const showMark = computed(() => !props.preview && !veiled.value)
// The picture opens on the feed and the profile tiles, never in the share sheet.
const picOpens = computed(() => showPic.value && props.interactive && !props.preview && !props.look)
const caption = computed(() => captionOf(props.lesson))
function edit() { if (props.editable && !props.editing) emit('edit') }
function onEditKey(e) {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); edit() }
}
function onKey(e) {
  if (!props.interactive || e.target !== e.currentTarget) return
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
}
</script>

<template>
  <article class="lc" :class="{ wide, tap: interactive, sheet: !!look, veiled, clear: look === 'clear', editing, updated }" data-testid="lesson-card"
           :role="interactive ? 'link' : null" :tabindex="interactive ? 0 : null"
           :aria-label="interactive ? (star || lead.text) : null" @click="open" @keydown="onKey">
    <div class="lc-pic" :class="{ fan: !showPic }" :data-testid="showPic ? null : 'lesson-card-cards-lead'">
      <PictureOpen v-if="picOpens" class="lc-open" data-testid="lesson-card-picture-open" :src="lesson.image_url" :caption="caption"
                   img-testid="lesson-card-picture" @error="picFailed = true" />
      <img v-else-if="showPic" :src="lesson.image_url" alt="" class="lc-img" data-testid="lesson-card-picture" @error="picFailed = true" />
      <template v-else>
        <span v-for="(c, i) in cards" :key="i" class="c" aria-hidden="true">
          <img v-if="c.image_url && !failedArt[i]" :src="c.image_url" alt="" :class="{ rev: c.reversed }"
               @error="failedArt = { ...failedArt, [i]: true }" />
        </span>
      </template>
      <MysticMark v-if="showMark" :mystic="mystic" />
    </div>
    <div class="lc-body">
      <p class="lc-li" data-testid="lesson-card-lead"><i v-if="lead.key" class="li-k">{{ lead.key }}</i>{{ lead.key ? ' ' : '' }}{{ lead.text }}</p>
      <slot v-if="editing" name="edit" />
      <div v-else-if="editable" class="lc-lsw">
        <h3 class="lc-ls lc-ls-tap" data-testid="lesson-card-lesson" role="button" tabindex="0"
            :aria-label="`Edit the lesson: ${star}`" @click.stop="edit" @keydown="onEditKey">{{ star }}</h3>
        <button type="button" class="lc-edit" data-testid="lesson-card-edit" @click.stop="edit"><span class="lc-edit-c">✎ Edit</span></button>
      </div>
      <h3 v-else class="lc-ls" data-testid="lesson-card-lesson">{{ star }}</h3>
      <div class="lc-foot">
        <button v-if="canSpeak" type="button" class="lc-res lc-res-btn" :class="{ on: spoke }" :aria-pressed="spoke ? 'true' : 'false'"
                :disabled="busy" data-testid="lesson-card-response"
                @click.stop="toggleSpoke" @keydown.enter.stop @keydown.space.stop><span class="st">✦</span> {{ SPOKE_SHORT }}<span v-if="count" class="n"> · {{ count }}</span></button>
        <span v-else class="lc-res" data-testid="lesson-card-response">✦ {{ SPOKE_SHORT }}<template v-if="count && !preview"> · {{ count }}</template></span>
        <span class="lc-credit">
          <span v-if="named" class="lc-av" data-testid="lesson-card-avatar" aria-hidden="true">
            <img v-if="avatar && !avFailed" :src="avatar" alt="" @error="avFailed = true" />
            <span v-else class="lc-av-ph">{{ initial }}</span>
          </span>
          <span class="lc-by" data-testid="lesson-card-credit"><template v-if="creditLink">Shared by <button type="button" class="lc-who"
              data-testid="lesson-card-credit-link" @click.stop="openCreditSeeker(lesson)" @keydown.enter.stop @keydown.space.stop>{{ who }}</button></template><template v-else>{{ credit }}</template></span>
        </span>
      </div>
    </div>
    <span v-if="updated && !editing" class="lc-upd" data-testid="lesson-card-updated">✦ Updated · the question and the card lines follow it</span>
  </article>
</template>

<style scoped>
.lc {
  --cream: #f4ecdc; --plum: #2a1845; --gold: #c9a94e; --golddk: #8a6a2a; --ink: #2a1845; --ink2: #3d2f55; --muted: #7a6a8f; --amethyst: #4e2776;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  position: relative; box-sizing: border-box; width: 300px; max-width: 100%; flex: 0 0 auto; border-radius: 18px; overflow: hidden;
  display: flex; flex-direction: column; background: var(--cream); color: var(--ink); text-align: left;
  box-shadow: 0 24px 46px -26px rgba(0, 0, 0, .95); border: 1px solid rgba(231, 201, 131, .22);
  transition: transform .25s ease, box-shadow .25s ease;
}
.lc *, .lc *::before, .lc *::after { box-sizing: border-box; }
.lc img { display: block; }
.lc.wide { width: 100%; }
.lc.tap { cursor: pointer; }
.lc.tap:hover { transform: translateY(-2px); box-shadow: 0 30px 50px -24px rgba(0, 0, 0, 1); }
.lc.tap:focus-visible { outline: 2px solid #e7c983; outline-offset: 3px; }
/* The picture at 3:2; the card's hover leans it in, and slides out the mark's name. */
.lc-pic { position: relative; flex: none; aspect-ratio: 3 / 2; background: var(--plum); overflow: hidden; }
.lc-img { width: 100%; height: 100%; object-fit: cover; transition: transform .8s cubic-bezier(.2, .8, .2, 1); }
.lc.tap:hover .lc-img { transform: scale(1.04); }
.lc-pic .lc-open { position: absolute; inset: 0; }
.lc-open :deep(.po-img) { transition: transform .8s cubic-bezier(.2, .8, .2, 1); }
.lc.tap:hover .lc-open :deep(.po-img) { transform: scale(1.04); }
.lc.tap:hover :deep(.mk-t) { max-width: 180px; padding: 0 8px; }
/* No picture: the four cards fanned on the plum table. */
.lc-pic.fan { display: flex; align-items: center; justify-content: center; --cw: 25%; --ov: -.1;
  background: radial-gradient(70% 90% at 50% 55%, #4b2a7a 0%, #2a1548 55%, #160a2b 100%); }
.lc-pic.fan::after { content: ''; position: absolute; left: 12%; right: 12%; bottom: 14%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(231, 201, 131, .35), transparent); }
.lc-pic.fan .c {
  position: relative; z-index: 1; flex: none; width: var(--cw); aspect-ratio: 2 / 3; border-radius: 6px; overflow: hidden;
  border: 1px solid rgba(231, 201, 131, .85); background: var(--plum); box-shadow: 0 14px 22px -10px rgba(0, 0, 0, .9);
  margin: 0 calc(var(--cw) * var(--ov)); transform: translateY(var(--y, 0)) rotate(var(--r, 0deg));
  transition: transform .45s cubic-bezier(.2, .8, .2, 1), margin .45s cubic-bezier(.2, .8, .2, 1);
}
.lc.tap:hover .lc-pic.fan { --ov: .02; }
.lc-pic.fan .c img { width: 100%; height: 100%; object-fit: cover; }
.lc img.rev { transform: rotate(180deg); }
.lc-pic.fan .c:nth-child(1) { --r: -8deg; --y: 7%; } .lc-pic.fan .c:nth-child(2) { --r: -2.5deg; }
.lc-pic.fan .c:nth-child(3) { --r: 2.5deg; } .lc-pic.fan .c:nth-child(4) { --r: 8deg; --y: 7%; }
.lc-pic.fan .c:nth-child(5) { --r: 12deg; --y: 14%; }

.lc-body { flex: 1; display: flex; flex-direction: column; min-height: 0; padding: 14px 18px 4px; }
/* The lead-in: two lines held, so a row lines up. */
.lc-li { margin: 0 0 8px; font: 500 16px/1.35 var(--serif); color: var(--ink2); overflow-wrap: anywhere;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; overflow: hidden; min-height: calc(2 * 1.35em); }
.li-k { font-style: italic; color: var(--muted); margin-right: .2em; }
/* The lesson, the star: three lines held. */
.lc-ls { margin: 0; font: italic 600 25px/1.13 var(--serif); color: var(--ink); text-wrap: balance; letter-spacing: -.004em; overflow-wrap: anywhere;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; line-clamp: 3; overflow: hidden; min-height: calc(3 * 1.13em); }
.lc-foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0 8px; margin-top: auto; padding: 8px 0 4px; flex: none; min-width: 0;
  font: 500 12px var(--ui); color: var(--muted); }
.lc-res { flex: none; white-space: nowrap; color: var(--amethyst); font: 600 13px var(--ui); }
/* The toggle keeps a 44px tap target inside the slim foot. */
.lc-res-btn { display: inline-flex; align-items: center; gap: 6px; height: 44px; padding: 0 10px; margin-left: -10px; border: 0; border-radius: 22px;
  background: none; cursor: pointer; }
.lc-res-btn .st { display: inline-block; transition: transform .5s cubic-bezier(.2, .8, .2, 1), color .2s; }
.lc-res-btn .n { font-weight: 500; color: var(--muted); }
.lc-res-btn.on { color: var(--golddk); }
.lc-res-btn.on .st { transform: rotate(144deg) scale(1.25); color: var(--gold); }
.lc-res-btn:disabled { cursor: default; }
.lc-res-btn:focus-visible { outline: 2px solid var(--gold); outline-offset: -6px; }
/* The credit: one line, the sharer's avatar first. On a narrow card it takes
   its own line (right-aligned) rather than cutting the name; only a name
   longer than the whole card ends in "…". */
.lc-credit { display: flex; align-items: center; justify-content: flex-end; gap: 7px; flex: 0 1 auto; min-width: 0; height: 24px; margin-left: auto; }
.lc-av { width: 24px; height: 24px; border-radius: 50%; overflow: hidden; flex: none; background: var(--plum); border: 1px solid var(--gold); }
.lc-av img { width: 100%; height: 100%; object-fit: cover; }
.lc-av-ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font: 600 12px/1 var(--display); color: var(--gold); }
/* No clip here: the name's 44px tap target reaches above and below the 24px line. */
.lc-by { display: flex; align-items: center; gap: .28em; font: italic 500 14px/24px var(--serif); color: var(--muted); min-width: 0; white-space: nowrap; }
.lc-who {
  display: block; min-width: 44px; height: 44px; margin: -10px 0; padding: 0; border: 0; background: none; cursor: pointer;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left;
  font: inherit; line-height: 44px; color: var(--amethyst); text-decoration: underline; text-decoration-color: rgba(78, 39, 118, .4); text-underline-offset: 3px;
}
.lc-who:hover { text-decoration-color: currentColor; }
.lc-who:focus-visible { outline: 2px solid #e7c983; outline-offset: 1px; border-radius: 4px; }
/* The share sheet (rev 13): the lesson centred; the look tells the switch's story. */
.lc.sheet .lc-li, .lc.sheet .lc-ls { text-align: center; }
.lc.sheet .lc-pic::after { content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity .6s ease;
  background: linear-gradient(180deg, rgba(244, 236, 220, .55), rgba(244, 236, 220, .35)); }
.lc.sheet .lc-img, .lc.sheet .lc-pic.fan .c { transition: filter .6s ease; }
.lc.sheet .lc-body { position: relative; }
.lc.sheet .lc-li, .lc.sheet .lc-ls, .lc.sheet .lc-foot { transition: opacity .6s ease; }
.lc.veiled .lc-pic::after { opacity: 1; }
.lc.veiled .lc-img, .lc.veiled .lc-pic.fan .c { filter: grayscale(.65) brightness(.9); }
/* The words at 62% (the Edit chip stays clear); full strength while editing. */
.lc.veiled .lc-li, .lc.veiled .lc-ls, .lc.veiled .lc-foot { opacity: .62; }
.lc.veiled.editing .lc-li, .lc.veiled.editing .lc-foot { opacity: 1; }
/* The lesson is visibly editable: a tap on it, or the chip at its corner. */
.lc-lsw { position: static; }
.lc-ls-tap { cursor: text; border-radius: 6px; }
.lc-ls-tap:hover { background: rgba(201, 169, 78, .1); }
.lc-ls-tap:focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; }
/* A 44px tap target holding a small chip, on the seam between the picture and the words (never over them). */
.lc-edit {
  position: absolute; top: -22px; right: 6px; z-index: 1; display: grid; place-items: center; min-width: 44px; height: 44px; padding: 0 6px;
  border: 0; background: none; cursor: pointer; border-radius: 22px;
}
.lc-edit-c {
  display: block; height: 26px; padding: 0 10px; border-radius: 13px; border: 1px solid rgba(201, 169, 78, .7); background: #fffaf0; color: var(--golddk);
  font: 600 11.5px/24px var(--ui); letter-spacing: .02em; white-space: nowrap; box-shadow: 0 4px 10px -6px rgba(42, 24, 69, .5);
}
.lc-edit:hover .lc-edit-c { border-color: var(--gold); background: #fff; }
.lc-edit:focus-visible { outline: none; }
.lc-edit:focus-visible .lc-edit-c { outline: 2px solid var(--amethyst); outline-offset: 2px; }
/* Updated in place. */
.lc.updated { box-shadow: 0 0 0 2px rgba(231, 201, 131, .9), 0 0 24px rgba(231, 201, 131, .35), 0 24px 46px -26px rgba(0, 0, 0, .95); }
.lc.updated .lc-ls { animation: lc-new .9s ease both; }
@keyframes lc-new { from { opacity: .2; } }
.lc-upd { display: block; padding: 0 18px 12px; text-align: center; font: 600 11.5px var(--ui); color: var(--golddk); }
@media (max-width: 767px) {
  .lc-pic { aspect-ratio: 16 / 10; }
  .lc-pic.fan { --cw: 21%; }
  .lc-ls { font-size: 26px; min-height: 0; }
  .lc-li { min-height: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .lc, .lc-img, .lc-pic.fan .c, .lc-res-btn .st { transition: none; }
  .lc.sheet .lc-pic::after, .lc.sheet .lc-img, .lc.sheet .lc-pic.fan .c, .lc.sheet .lc-li, .lc.sheet .lc-ls, .lc.sheet .lc-foot { transition: none; }
  .lc.updated .lc-ls { animation: none; }
  .lc.tap:hover { transform: none; }
  .lc.tap:hover .lc-img, .lc.tap:hover .lc-open :deep(.po-img) { transform: none; }
}
</style>
